// ─── Raport — logika murni halaman (tanpa React) ─────────────────────────────
//
// Cermin aturan server (src/utils/raport/rubrik.js) untuk tanggapan INSTAN saat trainer
// mengetuk nilai: nilai bulan, sahnya komentar, status bulan. Server tetap otoritatif —
// setelah tersimpan, isian murid diganti jawaban server. Contoh angkanya diuji di
// RaportPage.test.jsx dengan kasus yang sama dengan tests/backend/test_raport.js, supaya
// kedua sisi tak diam-diam berbeda pendapat.

export const SKALA = ["NI", "F", "G", "VG", "O"];
const ANGKA = { NI: 1, F: 2, G: 3, VG: 4, O: 5 };
export const SATU_NILAI = new Set(["attendance", "punctuality"]);
export const NAMA_KOLOM = ["Rekaman 1", "Rekaman 2", "Nilai Guru"];
export const NAMA_RUBRIK = { speaking: "Speaking", learning: "Learning Record", gabungan: "Speaking & Learning Record" };
const BULAN_SINGKAT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

/** Rata-rata nilai yang terisi, dibulatkan ke skala terdekat (3,5 → VG). "" bila kosong. */
export function rataNilai(daftar) {
  const angka = (daftar || []).map((g) => ANGKA[g]).filter(Boolean);
  if (!angka.length) return "";
  const r = Math.round(angka.reduce((a, b) => a + b, 0) / angka.length);
  return SKALA[Math.max(1, Math.min(5, r)) - 1];
}

/** Nilai satu aspek sebulan. Attendance & Punctuality hanya membaca kolom 1. */
export const nilaiBulan = (aspek, isian) =>
  (SATU_NILAI.has(aspek) ? rataNilai([(isian || [])[0]]) : rataNilai(isian));

/** Kalimat komentar untuk (rubrik, aspek, nilai) dari bank. */
export const pilihanKomentar = (bank, rubrik, aspek, nilai) =>
  ((((bank || {}).rubrik || {})[rubrik] || {}).komentar || {})[aspek]?.[nilai] || [];

/** Komentar sah = dipilih untuk NILAI bulan yang sekarang, dari versi bank yang sekarang. */
export function komentarSah(bank, rubrik, aspek, nilai, komentar) {
  if (!nilai || !komentar || komentar.pilihan == null) return false;
  if (komentar.nilai !== nilai || Number(komentar.versi) !== Number(bank.versi)) return false;
  return komentar.pilihan >= 0 && komentar.pilihan < pilihanKomentar(bank, rubrik, aspek, nilai).length;
}

/** "lengkap" | "sebagian" | "kosong" dari data aspek satu bulan ({ aspek: { isian, komentar } }). */
export function statusBulan(bank, rubrik, aspekList, data) {
  const ada = aspekList.some((a) => (data[a]?.isian || []).some(Boolean) || data[a]?.komentar?.pilihan != null);
  if (!ada) return "kosong";
  const lengkap = aspekList.every((a) => {
    const n = nilaiBulan(a, data[a]?.isian);
    return n && komentarSah(bank, rubrik, a, n, data[a]?.komentar);
  });
  return lengkap ? "lengkap" : "sebagian";
}

/** Boleh terbit: tiap bulan lengkap ATAU kosong, minimal satu lengkap. */
export const siapTerbit = (status) =>
  status.every((s) => s === "lengkap" || s === "kosong") && status.some((s) => s === "lengkap");

/**
 * Hitung ulang satu bulan setelah isian berubah di layar: nilai tiap aspek, sahnya
 * komentar, dan status bulannya. `bulan` dari server: { ke, status, aspek, acuan }.
 */
export function hitungBulan(bank, rubrik, aspekList, bulan) {
  const aspek = {};
  aspekList.forEach((a) => {
    const x = bulan.aspek[a] || { isian: [], komentar: { pilihan: null } };
    const nilai = nilaiBulan(a, x.isian);
    aspek[a] = { ...x, nilai, komentar: { ...x.komentar, sah: komentarSah(bank, rubrik, a, nilai, x.komentar) } };
  });
  return { ...bulan, aspek, status: statusBulan(bank, rubrik, aspekList, aspek) };
}

/** Ringkasan murid dari ketiga bulannya. */
export function ringkasMurid(bulan) {
  const status = bulan.map((b) => b.status);
  return { lengkap: status.every((s) => s === "lengkap"), siapTerbit: siapTerbit(status) };
}

/** Komentar akhir bulan: kalimat-kalimat yang SAH, urut aspek; "Ananda" → nama murid. */
export function komentarAkhir(bank, rubrik, aspekList, bulan, nama) {
  return aspekList
    .map((a) => {
      const x = bulan.aspek[a];
      if (!x || !x.komentar?.sah) return "";
      return (pilihanKomentar(bank, rubrik, a, x.nilai)[x.komentar.pilihan] || "").replace(/Ananda/g, nama || "Ananda");
    })
    .filter(Boolean)
    .join(" ");
}

/** "2026-10" → "Okt". */
export const namaBulan = (iso) => BULAN_SINGKAT[Number(String(iso).split("-")[1]) - 1] || "";

/** Detik UNIX → "20 Des 2026, 10.15" dalam jam Jakarta, di perangkat mana pun. */
export function waktuWIB(detik) {
  if (!detik) return "";
  const p = {};
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jakarta", year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23"
  }).formatToParts(new Date(detik * 1000)).forEach((x) => { p[x.type] = x.value; });
  return `${Number(p.day)} ${BULAN_SINGKAT[Number(p.month) - 1]} ${p.year}, ${p.hour}.${p.minute}`;
}

/**
 * Status raport satu murid di daftar kelas:
 *   "terbit"  — raport terbitnya sama dengan isian sekarang
 *   "siap"    — siap terbit, belum terbit atau isiannya berubah sejak terbit
 *   "belum"   — belum lengkap
 * `berubah` terpisah: pernah terbit, tapi isiannya kini berbeda.
 */
export function statusRaport(m) {
  const terkini = !!m.terbit && !m.terbit.berubah;
  return {
    status: terkini ? "terbit" : m.siapTerbit ? "siap" : "belum",
    berubah: !!m.terbit && m.terbit.berubah
  };
}

/** Kalimat acuan kehadiran untuk ditampilkan di atas tombol Attendance / Punctuality. */
export function teksAcuan(aspek, acuan) {
  if (!acuan) return "Bulan ini belum berjalan.";
  if (!acuan.pertemuan) return "Belum ada pertemuan tercatat untuk murid ini di kelas ini.";
  if (aspek === "attendance") {
    return `Hadir ${acuan.hadir} dari ${acuan.pertemuan} pertemuan${acuan.persen == null ? "" : ` (${acuan.persen}%)`}`;
  }
  const bagian = [
    `On Time ${acuan.tepat}`,
    `Late ${acuan.telat}`,
    `Very Late ${acuan.sangatTelat}`,
    acuan.tanpaKeterangan ? `Tanpa keterangan ${acuan.tanpaKeterangan}` : ""
  ].filter(Boolean);
  return bagian.join(" · ");
}

export const ALASAN_TERKUNCI = {
  "menunggu-kelas-utama": "Murid ini ada di dua kelas raport. Menunggu team leader memilih kelas utamanya.",
  "kelas-utama-lain": "Raport murid ini diisi di kelas utamanya.",
  "beda-cabang": "Murid ini terdaftar di cabang lain; raportnya mengikuti term cabangnya."
};
